import { Link } from "react-router-dom";
import { ArrowUpRight, Radar, GraduationCap, FileCheck2, Wrench } from "lucide-react";
import { IMAGES } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";

const PILLARS = [
  {
    icon: Radar,
    num: "01",
    title: "Positive Utility Location",
    desc: "The law requires positive location of existing utilities before excavation — no matter the method. Every utility we cross is verified for location and depth before work begins.",
  },
  {
    icon: Wrench,
    num: "02",
    title: "Hydro-Vac Potholing",
    desc: "Trailer-mounted Jet Vacs expose buried facilities with high-pressure water and vacuum instead of steel — an 8-inch hole, 10 feet deep, in as little as ten minutes with zero damage risk.",
  },
  {
    icon: GraduationCap,
    num: "03",
    title: "Trained, Tenured Crews",
    desc: "Working foremen with many years of underground experience and low turnover — plus one of the few in-house Ditch Witch directional boring simulators for continuous operator training.",
  },
  {
    icon: FileCheck2,
    num: "04",
    title: "Insured & Compliant",
    desc: "We meet all insurance requirements including general liability and workers compensation. Current coverage certificates available on request, customizable to your specifications.",
  },
];

const PROCESS = [
  { step: "P1", label: "Locate", desc: "Ticket and identify every utility in the work zone" },
  { step: "P2", label: "Expose", desc: "Hydro-vac pothole to confirm exact position and depth" },
  { step: "P3", label: "Verify", desc: "Crew briefed on crossings before equipment mobilizes" },
  { step: "P4", label: "Build", desc: "Excavate, bore, or trench with verified clearance" },
];

export default function Safety() {
  return (
    <>
      <PageHero
        testId="safety-hero"
        eyebrow="Safety First // Every Jobsite"
        lines={["Know what's", "below."]}
        sub="Underground work means working around live utilities. Our safety discipline is built on positive location, non-destructive exposure, and crews trained on the equipment they run."
        image={IMAGES.augerBoring}
      />

      <section data-testid="safety-pillars" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>The Protocol</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              Four pillars before we break ground
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.06}>
                <div className="group h-full bg-obsidian p-6 sm:p-10 transition-colors hover:bg-[#0d1420]">
                  <div className="flex items-start justify-between">
                    <p.icon className="h-8 w-8 text-amber-400" />
                    <span className="font-mono text-sm text-slate-600">/{p.num}</span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl sm:text-3xl font-bold uppercase text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="safety-process" className="border-y border-amber-500/25 bg-gradient-to-b from-amber-500/5 to-transparent py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
              Dig Safe Sequence
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              Locate. Expose. Verify. Build.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div className="h-full bg-obsidian p-6 sm:p-8">
                  <span className="font-mono text-xs font-bold text-amber-400">{s.step}</span>
                  <h3 className="mt-3 font-display text-3xl font-extrabold uppercase text-white">
                    {s.label}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-3xl text-sm leading-relaxed text-slate-400">
              Whether the method is backhoe, trencher, or directional bore machine — all
              utilities being crossed are verified first. It's the law, and it's how we've
              worked for 46 years.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="safety-cta" className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              Need utility locating on your project?
            </h2>
            <Link
              to="/request-bid"
              data-testid="safety-cta-contact"
              className="mt-8 inline-flex items-center gap-2 bg-amber-500 px-8 py-4 text-sm font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-amber-400"
            >
              Request Vacuum Potholing <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
